// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Download, Calendar, TrendingUp, AlertTriangle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from 'date-fns';

const ReportsTab: React.FC = () => {
  const [timePeriod, setTimePeriod] = useState('30');

  const daysBack = parseInt(timePeriod);
  const startDate = subDays(new Date(), daysBack);

  // Fetch all payout data
  const { data: payoutsData, isLoading } = useQuery({
    queryKey: ['financial-reports', timePeriod],
    queryFn: async () => {
      const dateFilter = startDate.toISOString();

      // Graphics vendor payouts
      const { data: vendorPayouts } = await supabase
        .from('graphics_vendor_payouts')
        .select('amount, status, created_at')
        .gte('created_at', dateFilter);

      // Press payouts
      const { data: pressPayouts } = await supabase
        .from('press_payouts')
        .select('amount, status, created_at')
        .gte('created_at', dateFilter);

      // Scout payouts
      const { data: scoutPayouts } = await supabase
        .from('scout_payouts')
        .select('amount, status, created_at')
        .gte('created_at', dateFilter);

      // Affiliate payouts
      const { data: affiliatePayouts } = await supabase
        .from('affiliate_payouts')
        .select('amount, status, created_at')
        .gte('created_at', dateFilter);

      return {
        vendors: vendorPayouts || [],
        press: pressPayouts || [],
        scouts: scoutPayouts || [],
        affiliates: affiliatePayouts || [],
      };
    },
  });

  // Fetch vendor performance
  const { data: vendorStats } = useQuery({
    queryKey: ['vendor-performance', timePeriod],
    queryFn: async () => {
      const { data: vendors } = await supabase
        .from('graphics_vendor_profiles')
        .select(`
          id,
          business_name,
          rating_average,
          review_count,
          graphics_orders!graphics_orders_vendor_id_fkey (
            id,
            status,
            total_price,
            created_at
          )
        `)
        .limit(4);

      return vendors?.map(vendor => {
        const orders = vendor.graphics_orders || [];
        const completedOrders = orders.filter(o => o.status === 'delivered' || o.status === 'completed');
        const totalRevenue = completedOrders.reduce((sum, o) => sum + (o.total_price || 0), 0);
        const completionRate = orders.length > 0 ? (completedOrders.length / orders.length) * 100 : 0;

        return {
          vendor: vendor.business_name,
          orders: orders.length,
          revenue: totalRevenue,
          rating: vendor.rating_average || 0,
          completion: Math.round(completionRate),
        };
      }).sort((a, b) => b.revenue - a.revenue) || [];
    },
  });

  // Calculate aggregated data
  const aggregatedData = useMemo(() => {
    if (!payoutsData) return null;

    const sumAmount = (payouts: any[]) => 
      payouts.reduce((sum, p) => sum + (p.amount || 0), 0);

    const vendorTotal = sumAmount(payoutsData.vendors);
    const pressTotal = sumAmount(payoutsData.press);
    const scoutTotal = sumAmount(payoutsData.scouts);
    const affiliateTotal = sumAmount(payoutsData.affiliates);

    // Group by month for trend
    const allPayouts = [
      ...payoutsData.vendors.map(p => ({ ...p, category: 'vendors' })),
      ...payoutsData.press.map(p => ({ ...p, category: 'press' })),
      ...payoutsData.scouts.map(p => ({ ...p, category: 'scouts' })),
      ...payoutsData.affiliates.map(p => ({ ...p, category: 'affiliates' })),
    ];

    const monthlyData = eachMonthOfInterval({
      start: subMonths(new Date(), 5),
      end: new Date(),
    }).map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);
      
      const monthPayouts = allPayouts.filter(p => {
        const payoutDate = new Date(p.created_at);
        return payoutDate >= monthStart && payoutDate <= monthEnd;
      });

      return {
        month: format(month, 'MMM'),
        total: monthPayouts.reduce((sum, p) => sum + (p.amount || 0), 0),
      };
    });

    // Calculate projections based on current rates
    const cashFlowData = [
      { 
        period: '30 days', 
        vendors: vendorTotal, 
        press: pressTotal, 
        scouts: scoutTotal, 
        affiliates: affiliateTotal 
      },
      { 
        period: '60 days', 
        vendors: vendorTotal * 1.8, 
        press: pressTotal * 1.8, 
        scouts: scoutTotal * 1.8, 
        affiliates: affiliateTotal * 1.8 
      },
      { 
        period: '90 days', 
        vendors: vendorTotal * 2.5, 
        press: pressTotal * 2.5, 
        scouts: scoutTotal * 2.5, 
        affiliates: affiliateTotal * 2.5 
      },
    ];

    const payeeBreakdown = [
      { name: 'Graphics Vendors', value: vendorTotal, color: '#002E62' },
      { name: 'Press Members', value: pressTotal, color: '#BC8D09' },
      { name: 'Scouts', value: scoutTotal, color: '#0EA5E9' },
      { name: 'Affiliates', value: affiliateTotal, color: '#10B981' },
    ];

    return {
      cashFlowData,
      payeeBreakdown,
      monthlyTrend: monthlyData,
      totalProjected: vendorTotal + pressTotal + scoutTotal + affiliateTotal,
    };
  }, [payoutsData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
  };

  const exportReport = () => {
    toast.success('Generating comprehensive financial report...');
  };

  if (isLoading || !aggregatedData) {
    return <div className="flex items-center justify-center p-8">Loading financial data...</div>;
  }

  const { cashFlowData, payeeBreakdown, monthlyTrend, totalProjected } = aggregatedData;
  const vendorPerformance = vendorStats || [];

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Financial Analytics</h2>
          <p className="text-muted-foreground">Comprehensive financial reports and projections</p>
        </div>
        <div className="flex gap-2">
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="60">Last 60 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
              <SelectItem value="365">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportReport}>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Cash Flow Projection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-secondary" />
            Cash Flow Projections
          </CardTitle>
          <CardDescription>Expected payouts by category over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis tickFormatter={(value) => `$${value / 1000}k`} />
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
                <Legend />
                <Bar dataKey="vendors" fill="#002E62" name="Vendors" />
                <Bar dataKey="press" fill="#BC8D09" name="Press" />
                <Bar dataKey="scouts" fill="#0EA5E9" name="Scouts" />
                <Bar dataKey="affiliates" fill="#10B981" name="Affiliates" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Next 30 Days Projection</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalProjected)}</div>
            <div className="flex items-center text-sm text-green-600 mt-1">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>+15% from last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">High Payout Period Alert</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">Jun 15-30</div>
            <div className="flex items-center text-sm text-orange-600 mt-1">
              <AlertTriangle className="h-4 w-4 mr-1" />
              <span>Expected: {formatCurrency(45000)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avg Monthly Payouts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(monthlyTrend.reduce((sum, m) => sum + m.total, 0) / monthlyTrend.length)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payee Category Breakdown & Monthly Trend */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Payout Distribution by Category</CardTitle>
            <CardDescription>Total payouts by payee type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={payeeBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {payeeBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value as number)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Payout Trend</CardTitle>
            <CardDescription>Total payouts over the last 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${value / 1000}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value as number)} />
                  <Line type="monotone" dataKey="total" stroke="#002E62" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vendor Performance Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Top Vendor Performance</CardTitle>
          <CardDescription>Revenue, completion rates, and customer satisfaction</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {vendorPerformance.map((vendor, index) => (
              <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <p className="font-semibold">{vendor.vendor}</p>
                  <p className="text-sm text-muted-foreground">{vendor.orders} orders completed</p>
                </div>
                <div className="flex gap-6 items-center">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="font-semibold">{formatCurrency(vendor.revenue)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Rating</p>
                    <p className="font-semibold">{vendor.rating} ⭐</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Completion</p>
                    <Badge variant={vendor.completion >= 95 ? 'default' : 'secondary'}>
                      {vendor.completion}%
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsTab;
